import { type CollectionView, EventListener, KeyCodes, View, type ViewData } from "silver";

let ai = 0;
const AUTOCOMPLETE_ID = () => {
	return "core-autocomplete-" + (++ai);
};
const HTML: string = `
	<div class="autoComplete {{ open? | '-open' }}">
		<div class="autoComplete_shade" data-myon-click="handleShadeClick"></div>
		<div class="g_screenReader" data-myon-attr-id="{{ inputId }}-instructions">{{ instructionText }}</div>
		<div class="g_screenReader" aria-live="assertive">{{ resultCountMessage }}</div>
		<ul data-myon-attr-id="{{ inputId }}-listbox" class="autoComplete_list" role="listbox" data-myon-name="ul">
			<li role="option"
				aria-label="{{ label }}"
				data-myon-attr-id="{{ inputId }}-row-{{ index }}"
				data-myon-template="rows"
				data-myon-attr-class="autoComplete_listItem {{ targeted? | '-selected' }}"
				data-myon-mouseenter="handleRowMouseEnter"
				data-myon-mouseleave="handleRowMouseLeave"
				data-myon-click="handleRowClick">
				{{ html @noescape }}
			</li>
		</ul>
	</div>`;

interface AutoCompleteViewData<Row extends AutoCompleteRow> extends ViewData {
	rows: Array<AutoCompleteRowViewData<Row>>;
	resultCountMessage: string;
	open: boolean;
	inputId: string;
	instructionText: string;
}

interface AutoCompleteRowViewData<Row extends AutoCompleteRow> extends ViewData {
	index: number;
	targeted: boolean;
	html: string|HTMLElement;
	label: string;
	row: Row;
}

interface AutoCompleteView<Row extends AutoCompleteRow> extends View<AutoCompleteViewData<Row>> {
	rows: CollectionView<AutoCompleteRowViewData<Row>>;
	ul: HTMLUListElement;
}

export type AutoCompleteRow = {
	html?: HTMLElement|string | ((query: string) => HTMLElement|string);
	label: string;
	value: string;
};

type AutoCompleteEvent<Row extends AutoCompleteRow> = {
	sender: AutoComplete<Row>;
};

type AutoCompleteRowSelectedEvent<Row extends AutoCompleteRow> = AutoCompleteEvent<Row> & {
	originalEvent?: Event;
	row: Row;
};

export class AutoComplete<Row extends AutoCompleteRow = AutoCompleteRow> {

	public readonly onSelect = EventListener.create<AutoCompleteRowSelectedEvent<Row>>("select");
	public readonly onDeselect = EventListener.create<AutoCompleteEvent<Row>>("deselect");

	private readonly inputId: string;
	private view: AutoCompleteView<Row>;
	private timeout: number = -1;

	private dataCache: Record<string, Row[]> = { };
	private lastText: string|undefined = "";
	private currentData: Row[] = [];
	private targetedIndex = -1;
	public fetchDelay: number = 300;
	public requiredCharacterCount: number = 1;
	public autoSelect: boolean = true;
	public selectOnTab: boolean = true;
	public resultCountLabeler: (count: number) => string = (() => "");

	constructor(
		private inputEl: HTMLInputElement,
		private resultFetcher: (text: string) => Promise<Row[]>
	) {
		const parentNode = inputEl.parentNode;
		if (!parentNode) {
			throw new Error("Provided input is not mounted in the DOM");
		}
		this.inputId = inputEl.getAttribute("id") || "";

		if (! this.inputId) {
			this.inputId = AUTOCOMPLETE_ID();
			inputEl.setAttribute("id", this.inputId);
		}

		inputEl.setAttribute("aria-haspopup", "listbox");
		inputEl.setAttribute("aria-expanded", "false");

		this.inputEl.setAttribute("aria-autocomplete", "list");
		this.inputEl.setAttribute("aria-controls", `${this.inputId}-listbox`);
		this.inputEl.setAttribute("aria-activedescendant", "");

		this.view = new View<AutoCompleteViewData<Row>>(HTML, {
			handleRowMouseEnter: (e, { index }) => {
				this.targetIndex(index, false);
			},
			handleRowMouseLeave: (e, { index }) => {
				this.untargetIndex(index);
			},
			handleRowClick: (e) => {
				this.selectTargeted(e);
			},
			handleShadeClick: () => {
				this.clear();
			}
		}, {
			rows:               [],
			inputId:            this.inputId,
			resultCountMessage: "",
			open:               false,
			instructionText:    "",
			labelledBy:         ""
		}) as AutoCompleteView<Row>;

		if (parentNode.lastChild == inputEl) {
			parentNode.appendChild(this.view.element);
		} else {
			parentNode.insertBefore(this.view.element, inputEl.nextSibling);
		}

		this.init();
	}

	private get isOpen(): boolean {
		return this.view.dataset.open;
	}

	private set isOpen(value: boolean) {
		this.view.dataset.open = value;
		this.inputEl.setAttribute("aria-expanded", value ? "true" : "false");
	}

	private init() {
		this.inputEl.setAttribute("autocomplete", "off");
		this.inputEl.addEventListener("focus", () => {
			clearTimeout(this.timeout);
			this.inputEl.setSelectionRange(0, this.inputEl.value.length);
		});

		this.inputEl.addEventListener("change", () => this.handleInputChanged());
		this.inputEl.addEventListener("input", () => this.handleInputChanged());
		this.inputEl.addEventListener("keyup", ({ keyCode }) => {
			if ([ KeyCodes.UP_ARROW, KeyCodes.DOWN_ARROW, KeyCodes.ENTER, KeyCodes.TAB, KeyCodes.BACKSPACE, KeyCodes.ESC ].indexOf(keyCode) === -1) {
				this.handleInputChanged();
			}
		});

		this.inputEl.addEventListener("keydown", (event: KeyboardEvent) => {
			if (event.keyCode == KeyCodes.ESC) {
				this.untargetIndex(this.targetedIndex);
				this.clear();
				event.preventDefault();
			}
			if (event.keyCode == KeyCodes.UP_ARROW) {
				this.targetIndex((this.currentData.length + this.targetedIndex - 1) % this.currentData.length, true);
				event.preventDefault();
			}
			if (event.keyCode == KeyCodes.DOWN_ARROW) {
				this.targetIndex((this.targetedIndex + 1) % this.currentData.length, true);
				event.preventDefault();
			}
			if (event.keyCode == KeyCodes.ENTER) {
				if (this.isOpen) {
					event.preventDefault();
				}
				this.selectTargeted(event);
			}
			if (event.keyCode == KeyCodes.TAB) {
				if (this.targetedIndex === -1) {
					this.clear();
				} else if (this.selectOnTab) {
					this.selectTargeted(event);
				} else {
					this.fillWithSelected();
				}
			}
		});
	}

	public clear() {
		this.view.rows.empty();
		this.view.set({ resultCountMessage: this.resultCountLabeler(0) });
		this.currentData = [];
		this.targetedIndex = -1;
		this.isOpen = false;
	}

	public set instructions(value: string) {
		this.view.dataset.instructionText = value;
		this.inputEl.setAttribute("aria-describedby", `${this.inputId}-instructions`);
	}

	public set labelledBy(id: string) {
		this.view.ul.setAttribute("aria-labelledby", id);
	}

	private async handleInputChanged(): Promise<void> {
		const text = this.inputEl.value;
		const cacheKey = text.toUpperCase();
		if (this.lastText == cacheKey) {
			return;
		}
		this.onDeselect.trigger({ sender: this });
		this.updateRowHtml(text);

		const rows = await this.fetch(text);
		if (document.activeElement !== this.inputEl || rows === false) {
			return;
		}

		this.view.rows.empty();
		rows.forEach((row, i) => {
			this.view.rows.add(String(i), {
				html:     this.getRowHtml(row, text),
				label:    row.label,
				row,
				targeted: false,
				index:    i
			});
		});

		this.view.set({ resultCountMessage: this.resultCountLabeler(rows.length) });

		this.isOpen = true;
		this.currentData = rows;
		this.targetIndex(this.autoSelect ? 0 : -1, true);
	}

	private updateRowHtml(text: string): void {
		this.view.rows.forEach(row => {
			row.set({ html: this.getRowHtml(row.dataset.row, text) });
		});
	}

	/**
	 * `false` will be returned when the response should be ignored.
	 * @param text
	 */
	private async fetch(text: string): Promise<Row[]|false> {
		text = text.trim();
		const cacheKey = text.toUpperCase();
		clearTimeout(this.timeout);
		this.timeout = -1;
		if (text.length < this.requiredCharacterCount) {
			this.clear();
			return false;
		}
		if (this.dataCache.hasOwnProperty(cacheKey)) {
			return Promise.resolve(this.dataCache[cacheKey]);
		}

		return new Promise((resolve, reject) => {
			const handle = this.timeout = setTimeout(async () => {
				try {
					const rows = await this.resultFetcher(text);
					this.dataCache[cacheKey] = rows;
					if (this.timeout !== handle) {
						return resolve(false);
					}
					resolve(rows);
				} catch (err) {
					reject(err);
				}
			}, this.fetchDelay);
		});
	}

	private fillWithSelected(): Row|undefined {
		if (this.targetedIndex < 0) {
			(this.inputEl.form as HTMLFormElement).submit();
			return undefined;
		}
		const row = this.currentData[this.targetedIndex];
		this.lastText = row.value.toString().toUpperCase();
		this.inputEl.value = row.value;
		this.clear();
		return row;
	}

	private selectTargeted(event: Event): void {
		const row = this.fillWithSelected();
		if (!row) {
			return;
		}

		this.onSelect.trigger({ row, sender: this, originalEvent: event });
	}

	private untargetIndex(index: number): void {
		if (index === this.targetedIndex) {
			this.targetedIndex = -1;
			this.inputEl.removeAttribute("aria-activedescendant");
		}
		if (this.view.rows.has(String(index))) {
			this.view.rows.get(String(index)).set({ targeted: false });
		}
	}

	private targetIndex(index: number, scrollIntoView: boolean): void {
		if (this.targetedIndex !== -1) {
			this.untargetIndex(this.targetedIndex);
		}
		if (!this.view.rows.has(String(index))) {
			if (scrollIntoView) {
				this.view.element.scrollTop = 0;
			}
			return;
		}
		const row = this.view.rows.get(String(index));
		row.set({ targeted: true });
		this.inputEl.setAttribute("aria-activedescendant", `${this.inputId}-row-${index}`);
		this.targetedIndex = index;
		if (scrollIntoView) {
			row.element.scrollIntoView(false);
		}
	}

	private getRowHtml(row: Row, inputText: string): string|HTMLElement {
		if (!row.html) {
			return row.label;
		}
		if (typeof row.html === "function") {
			return row.html(inputText);
		}
		return row.html;
	}
}
